import { useEffect, useMemo, useState } from "react";
import { AlertCircle, CheckCircle2, Circle, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

const STEP_INTERVAL_MS = 650;

function StatusIcon({ state }) {
  if (state === "running") return <Loader2 className="h-4 w-4 animate-spin text-stone-500" />;
  if (state === "complete") return <CheckCircle2 className="h-4 w-4 text-emerald-600" />;
  if (state === "error") return <AlertCircle className="h-4 w-4 text-red-500" />;
  return <Circle className="h-4 w-4 text-stone-300" />;
}

function StepLine({ category, title, detail, state = "idle" }) {
  const tone =
    state === "complete"
      ? "border-emerald-100 bg-emerald-50/60"
      : state === "error"
        ? "border-red-100 bg-red-50/60"
        : state === "running"
          ? "border-stone-200 bg-stone-50"
          : "border-stone-200 bg-white";

  return (
    <div className={`rounded-lg border ${tone} p-3`}>
      <div className="flex items-start gap-3">
        <div className="mt-0.5">
          <StatusIcon state={state} />
        </div>
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase text-stone-500">{category}</div>
          <div className="mt-0.5 text-sm font-medium text-stone-900">{title}</div>
          {detail ? <div className="mt-1 text-xs leading-5 text-stone-600">{detail}</div> : null}
        </div>
      </div>
    </div>
  );
}

export default function AnalysisLog({ status, analysis, errorInfo }) {
  const { t } = useTranslation();
  const [activeStepIndex, setActiveStepIndex] = useState(-1);
  const isRunning = status === "running";
  const isSuccess = status === "success";
  const isError = status === "error";
  const market = analysis?.market;
  const investorCount = Array.isArray(analysis?.investors) ? analysis.investors.length : 0;

  useEffect(() => {
    if (status === "idle") {
      setActiveStepIndex(-1);
      return undefined;
    }

    if (status === "success") {
      setActiveStepIndex(5);
      return undefined;
    }

    if (status === "error") {
      setActiveStepIndex((current) => Math.max(current, 0));
      return undefined;
    }

    setActiveStepIndex(0);
    const intervalId = window.setInterval(() => {
      setActiveStepIndex((current) => Math.min(current + 1, 4));
    }, STEP_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [status]);

  const steps = useMemo(() => [
    {
      category: t("app:AiFunding.logInputCategory"),
      title: t("app:AiFunding.logInputTitle"),
      idleDetail: t("app:AiFunding.logInputIdle"),
      runningDetail: t("app:AiFunding.logInputRunning"),
      completeDetail: t("app:AiFunding.logInputComplete", {
        stage: analysis?.profile?.stageGuess || t("app:AiFunding.unknownValue"),
        goal: analysis?.profile?.fundingGoal || t("app:AiFunding.unknownValue"),
      }),
    },
    {
      category: t("app:AiFunding.logTaxonomyCategory"),
      title: t("app:AiFunding.logTaxonomyTitle"),
      idleDetail: t("app:AiFunding.logTaxonomyIdle"),
      runningDetail: t("app:AiFunding.logTaxonomyRunning"),
      completeDetail: t("app:AiFunding.logTaxonomyComplete", {
        industry: analysis?.primaryIndustry || t("app:AiFunding.unknownValue"),
      }),
    },
    {
      category: t("app:AiFunding.logMarketCategory"),
      title: t("app:AiFunding.logMarketTitle"),
      idleDetail: t("app:AiFunding.logMarketIdle"),
      runningDetail: t("app:AiFunding.logMarketRunning"),
      completeDetail: t("app:AiFunding.logMarketComplete", {
        count: market?.totalDeals ?? 0,
        known: market?.knownAmountDeals ?? 0,
      }),
    },
    {
      category: t("app:AiFunding.logInvestorsCategory"),
      title: t("app:AiFunding.logInvestorsTitle"),
      idleDetail: t("app:AiFunding.logInvestorsIdle"),
      runningDetail: t("app:AiFunding.logInvestorsRunning"),
      completeDetail: t("app:AiFunding.logInvestorsComplete", { count: investorCount }),
    },
    {
      category: t("app:AiFunding.logMemoCategory"),
      title: t("app:AiFunding.logMemoTitle"),
      idleDetail: t("app:AiFunding.logMemoIdle"),
      runningDetail: t("app:AiFunding.logMemoRunning"),
      completeDetail: t("app:AiFunding.logMemoComplete"),
    },
  ], [analysis, investorCount, market, t]);

  const getStepState = (index) => {
    if (isError && index === Math.max(activeStepIndex, 0)) return "error";
    if (isSuccess) return "complete";
    if (isRunning && index < activeStepIndex) return "complete";
    if (isRunning && index === activeStepIndex) return "running";
    return "idle";
  };

  return (
    <section className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm">
      <h2 className="text-xl font-semibold tracking-tight text-stone-950">
        {t("app:AiFunding.analysisLogTitle")}
      </h2>
      <p className="mt-2 text-sm leading-6 text-stone-600">
        {t("app:AiFunding.analysisLogSubtitle")}
      </p>

      <div className="mt-5 space-y-3">
        {steps.map((step, index) => {
          const stepState = getStepState(index);
          return (
            <StepLine
              key={step.title}
              category={step.category}
              title={step.title}
              detail={stepState === "complete" ? step.completeDetail : stepState === "idle" ? step.idleDetail : step.runningDetail}
              state={stepState}
            />
          );
        })}
      </div>

      {isError ? (
        <div className="mt-4 rounded-lg border border-red-100 bg-red-50/60 p-3 text-xs leading-5 text-red-700">
          <div className="font-medium">{errorInfo?.code}</div>
          <div>{errorInfo?.message}</div>
        </div>
      ) : null}
    </section>
  );
}
